import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, BookOpen, Home, Sprout } from "lucide-react";
import { Button } from "../components/ui/button";
import { SemearFontStyle, YHUHName, YHUSHAName } from "../components/SemearNames";

const FLAG_URL = "https://customer-assets.emergentagent.com/job_cf0b5491-f8c8-4451-aded-31dbb7c32d76/artifacts/rb7dp60t_Jonesaica%20Yahshar%27al%20flag%202.png";
const COAT_OF_ARMS = "https://customer-assets.emergentagent.com/job_92e13f48-99c4-452a-b014-1be6a3b8b28b/artifacts/e3352bxk_Mock%20up%20Jonesaica%20Yah%E2%80%99shar%E2%80%99al.png";

export default function About() {
  return (
    <div className="min-h-screen" data-testid="about-page">
      <SemearFontStyle />

      {/* Hero */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
              About The Kingdom
            </span>
            <h1 className="heading-hero mt-2 mb-6">
              The Kingdom of Yunah&apos;Shach&apos;Qal Yashar&apos;al
            </h1>
            <p className="body-large text-slate-300">
              A covenant gathering seeking the Ancient Path, restoring the Turah of <YHUHName /> (YaHUaH), and building households and communities according to His Instructions.
            </p>
          </div>
        </div>
      </section>

      {/* Identity Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-section text-royal-blue mb-6">Who We Are</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  The Kingdom of Yunah&apos;Shach&apos;Qal Yashar&apos;al is not established to replace the Kingdom of <YHUHName />, but to serve as a gathering place for those seeking to walk according to His Instructions while preparing themselves, their households, and future generations for His coming Kingdom.
                </p>
                <p>
                  We acknowledge <YHUHName /> as Creator of Ha&apos;Shamaym and Aratz, <YHUSHAName /> (YaHUSHA) as Mashyach and Kinsman Redeemer, and the Ruch Ha&apos;Qadash as the Spirit sent to guide, teach, correct, and strengthen His people.
                </p>
                <p>
                  We are a covenant people seeking to return to the Ancient Path spoken of in YaramYahu 6:16, restoring family order, righteous judgment, useful trades, agriculture, craftsmanship, record keeping, and community life according to Scripture.
                </p>
                <p>
                  Founded by <strong>YaramYahu BanYaHUaH and Shaba Sharah BatYaHUaH</strong>, this work began as a public declaration of allegiance to <YHUHName />, His Turah, and the gathering of His people from among the nations.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <img
                src={FLAG_URL}
                alt="Flag of Yunah'Shach'Qal Yashar'al"
                className="w-full max-w-md mx-auto rounded-sm shadow-xl"
              />
              <img
                src={COAT_OF_ARMS}
                alt="Coat of Arms"
                className="w-48 mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-parchment py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue text-center mb-8">Our Mission</h2>
          <div className="bg-white p-8 rounded-sm shadow-lg border-l-4 border-kingdom-gold">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Seeking the Ancient Path through the Turah of <YHUHName /> while restoring family, community, craftsmanship, agriculture, and the ancient ways entrusted to our fathers.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              We labor to teach the Instructions of <YHUHName />, proclaim the Kingdom of Ha&apos;Shamaym, preserve the Abary tongue, guard historical records and genealogies, and strengthen households through practical skills, righteous service, and covenant responsibility.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Our aim is not merely to separate from Babylonian systems, but to become a people who can live, work, build, grow food, preserve knowledge, care for the needy, and walk uprightly before <YHUHName />.
            </p>
          </div>
        </div>
      </section>

      {/* What We Are Building */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue text-center mb-12">
            What We Are Building
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card-official p-6 text-center">
              <BookOpen className="w-10 h-10 text-kingdom-gold mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold text-royal-blue mb-2">
                Turah Instruction
              </h3>
              <p className="text-sm text-slate-600">
                Scripture study, Abary tongue, family instruction, youth training, and teaching rooted in the Ancient Path.
              </p>
            </div>

            <div className="card-official p-6 text-center">
              <Users className="w-10 h-10 text-kingdom-gold mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold text-royal-blue mb-2">
                YaSHaRUN — The Beloved Upright of YaHUaH
              </h3>
              <p className="text-sm text-slate-600">
                A vetted body of people walking together through covenant, service, accountability, and righteous conduct.
              </p>
            </div>

            <div className="card-official p-6 text-center">
              <Sprout className="w-10 h-10 text-kingdom-gold mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold text-royal-blue mb-2">
                Self-Sustaining Life
              </h3>
              <p className="text-sm text-slate-600">
                Agriculture, water, food preservation, healing, trades, textiles, tools, and household production.
              </p>
            </div>

            <div className="card-official p-6 text-center">
              <Home className="w-10 h-10 text-kingdom-gold mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold text-royal-blue mb-2">
                Future Communities
              </h3>
              <p className="text-sm text-slate-600">
                Preparing households, ministries, and settlements able to function apart from systems that oppose <YHUHName />&apos;s Instructions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Foundation */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-center mb-8">Our Foundation</h2>
          <div className="space-y-6 text-slate-300 body-default leading-relaxed">
            <p>
              The foundation of this Kingdom is <YHUHName />, Creator of Ha&apos;Shamaym, Aratz, and Yum. We do not call Him by pagan titles.
            </p>
            <p>
              We acknowledge <YHUSHAName /> as the one sent by <YHUHName />, the Lamb who was slain, Mashyach, and Kinsman Redeemer. We do not call Him by pagan names or titles.
            </p>
            <p>
              We acknowledge the Ruch Ha&apos;Qadash sent by <YHUHName /> to guide His people into truth, correction, remembrance, endurance, and obedience.
            </p>
            <p>
              We reject idolatry, witchcraft, sorcery, secret societies, false worship, lawlessness, and every doctrine that leads people away from the Turah of <YHUHName />.
            </p>
            <p>
              We seek to restore the ancient ways of worship, family, land, food, healing, craftsmanship, record keeping, and covenant life according to Scripture.
            </p>
          </div>
        </div>
      </section>

      {/* Kingdom Order */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue text-center mb-8">
            Kingdom Order
          </h2>

          <div className="bg-parchment p-8 rounded-sm shadow-lg">
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                The Kingdom of Yunah&apos;Shach&apos;Qal Yashar&apos;al recognizes <YHUHName /> alone as Malak, Lawgiver, and Judge.
              </p>
              <p>
                The Turah of <YHUHName /> is the standard by which the Kingdom seeks to walk, judge matters, appoint stewards, settle disputes, and order community life.
              </p>
              <p>
                The work of the Kingdom is carried through elders, judges, heads of households, overseers, record keepers, craftsmen, watchmen, healers, farmers, teachers, and servants appointed according to character, fruit, wisdom, and calling.
              </p>
              <p>
                No position is to be obtained through money, popularity, self-exaltation, or influence. Appointment comes from <YHUHName />, while elders and overseers seek to discern and confirm the matter with prayer, witness, and righteous judgment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nationals */}
      <section className="bg-parchment py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue text-center mb-8">
            YaSHaRUN — The Beloved Upright of YaHUaH
          </h2>

          <div className="bg-white p-8 rounded-sm shadow-lg border-l-4 border-kingdom-gold">
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                YaSHaRUN — The Beloved Upright of YaHUaH — expresses the upright character of those who SHAMA: hear and do. No longer continually laying the foundation, they continue what they were taught, growing from milk to meat and beyond, steadfastly walking in the Turah of YaHUaH. This covenant relationship is not a commercial transaction.
              </p>
              <p>
                Prospective YaSHaRUN must confess belief in <YHUHName />, <YHUSHAName />, and the Ruch Ha&apos;Qadash; renounce religion and practices contrary to Scripture; walk according to the Turah; guard the Shabath and Muadym; reject witchcraft, sorcery, divination, spiritism, idolatry, and secret societies; and submit to vetting by appointed elders and overseers.
              </p>
              <p>
                Supporters may assist the work of the Kingdom without becoming YaSHaRUN, but support does not confer nationality, office, stewardship, leadership, privilege, or special standing.
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/become-national">
                <Button className="bg-royal-blue hover:bg-slate-800 text-white px-6">
                  Become YaSHaRUN
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/governance/declaration">
                <Button variant="outline" className="border-royal-blue text-royal-blue hover:bg-royal-blue/10 px-6">
                  Read the Declaration
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-kingdom-gold py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            Walk the Ancient Path With Us
          </h2>
          <p className="text-white/90 mb-6">
            Read the Declaration, study the Constitution, and learn how YaSHaRUN are being gathered, vetted, and prepared for the work ahead.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/governance/constitution">
              <Button className="bg-royal-blue hover:bg-slate-800 text-white px-6">
                Read Our Constitution
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/become-national">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-6">
                Become YaSHaRUN
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
